import React, { useState, useRef, useEffect } from "react";
import { string, object, func, oneOfType, bool, number } from "prop-types";
import { parse, isValid, format, getYear } from "date-fns";
import { Form } from "react-bootstrap";
import { usePopper } from "react-popper";
import FocusTrap from "focus-trap-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

const DatePickerInput = ({
  keyValue,
  handleDateChange,
  dateValue,
  disabled,
  inputPlaceholderText,
  fromAnalytics,
  fromTestMakerAnalytics,
  customStyle,
  fromYear,
  toYear,
}) => {
  const [selected, setSelected] = useState(
    keyValue ? dateValue[keyValue] : dateValue
  );
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const [popperElement, setPopperElement] = useState(null);

  useEffect(() => {
    setSelected(keyValue ? dateValue[keyValue] : dateValue);
  }, [keyValue, dateValue]);

  const popperRef = useRef(null);

  const popper = usePopper(popperRef.current, popperElement, {
    placement: "bottom-start",
  });

  const closePopper = () => {
    setIsPopperOpen(false);
  };

  const handleOnClick = () => {
    setIsPopperOpen(true);
  };

  const handleInputChange = (e) => {
    const date = parse(e.currentTarget.value, "yyyy-MM-dd", new Date());
    if (isValid(date)) {
      setSelected(date);
    } else {
      setSelected(undefined);
    }
  };

  const handleDaySelect = (date) => {
    // selecting the same date in the date picker gives an undefined value which presents an error when formatting.
    // we want the popper/dialog with the calendar to stay open and not have an error so we return out of the function.
    if (!isValid(date)) {
      return;
    }
    // specific to filterModal in results. Passing keyValue allows filterModal to store both the 'from' and 'to' dates in one object
    if (keyValue) {
      handleDateChange({
        ...dateValue,
        [keyValue]: format(date, "yyyy-MM-dd"),
      });
    } else {
      handleDateChange(date);
    }
    setSelected(format(date, "yyyy-MM-dd"));
    closePopper();
  };

  const getFromYear = () => {
    if (fromYear) {
      return fromYear;
    }
    return 2000;
  };

  const getToYear = () => {
    if (toYear) {
      return toYear;
    }
    return getYear(new Date());
  };

  const inputWidth = fromTestMakerAnalytics ? "25%" : "33%";

  const parsedSelectedDate = parse(selected, "yyyy-MM-dd", new Date());

  const getStyles = () => {
    if (customStyle) {
      return customStyle;
    }

    const style = {
      position: "relative",
    };
    if (!fromAnalytics) {
      style.width = inputWidth;
    }
    return style;
  };

  return (
    <div className="d-inline-block" style={getStyles()}>
      <div ref={popperRef}>
        <Form.Control
          type="text"
          className="datePickerInput"
          placeholder={inputPlaceholderText || "YYYY-MM-DD"}
          onClick={handleOnClick}
          value={selected}
          onChange={handleInputChange}
        />
      </div>
      {isPopperOpen && (
        <FocusTrap
          active
          focusTrapOptions={{
            initialFocus: false,
            allowOutsideClick: true,
            clickOutsideDeactivates: true,
            onDeactivate: closePopper,
            fallbackFocus: popperRef.current,
          }}
        >
          <div
            tabIndex={-1}
            className="dialog-sheet dateTimeInputDialog"
            {...popper.attributes.popper}
            ref={setPopperElement}
            role="dialog"
          >
            <DayPicker
              initialFocus={isPopperOpen}
              mode="single"
              defaultMonth={selected ? parsedSelectedDate : new Date()}
              selected={parsedSelectedDate}
              onSelect={handleDaySelect}
              disabled={disabled}
              fromYear={getFromYear()}
              toYear={getToYear()}
              captionLayout="dropdown"
              modifiersStyles={{
                selected: { backgroundColor: "#425CC7" },
                today: { border: "2px solid #425CC7" },
              }}
            />
          </div>
        </FocusTrap>
      )}
    </div>
  );
};

DatePickerInput.propTypes = {
  keyValue: string,
  dateValue: oneOfType([object, string]),
  handleDateChange: func,
  disabled: object,
  inputPlaceholderText: string,
  fromAnalytics: bool,
  fromTestMakerAnalytics: bool,
  customStyle: object,
  fromYear: number,
  toYear: number,
};

export default DatePickerInput;
